import { z } from 'zod';

/**
 * The hero/cosmetic authoring schema (ADR-0097 / ADR-0098 / ADR-0099 / ADR-0100). Content is authored per
 * HERO, each hero a self-contained directory:
 *
 *   content/<hero>/manifest.yaml              — the hero DEFINITION (archetype, status, places, forms, levels,
 *                                               defaultBundle, and its OWN `looks` → the DEFAULT bundle, ADR-0114)
 *   content/<hero>/bundles/<name>/bundle.yaml — an EXPANSION bundle of looks for that hero
 *
 * `heroArchetype` is INFERRED from the directory, not authored on the bundle; the default bundle's slug IS
 * the archetypeId (ADR-0100). Everything is validated at COMPILE time (`scripts/compile.ts`) so malformed
 * content fails the build loudly with a path-scoped message — never a silent bad catalog at runtime. The
 * compiler SYNTHESIZES the default {@link BundleFile} from the manifest (config + `looks`) and reads each
 * expansion from its `bundles/<name>/bundle.yaml`.
 *
 * The unlock-rule union mirrors play's `StageUnlock` VO VERBATIM (the 6 ADR-0061 reward kinds + the 2
 * play-native kinds, same bounds). This copy lets the package validate without importing the API; the
 * API's `parsePlayContent` re-validates every seeded row, so this is a convenience gate, not the authority.
 */

const SLUG = /^[a-z][a-z0-9_-]{0,63}$/;
// i18n keys may be camelCase (e.g. `play.hero.princess.queen`), so allow A–Z.
const I18N_KEY = /^[a-z][a-zA-Z0-9_.]{0,99}$/;

// The princess's historical wear regions (ADR-0098). Wear-slots are now hero-scoped PLACES (ADR-0099): each
// hero declares its own in `hero.yaml`.places, and a cosmetic's `place` is validated against them. This
// constant is retained only as the princess's canonical place set (the API seed asserts her places match it);
// it is NOT a core enum — other heroes (the robot's chassis/visor/…) declare entirely different places.
export const COSMETIC_CATEGORIES = ['outfit', 'headwear', 'face', 'back', 'background'] as const;
export const CONTENT_STATUSES = ['draft', 'active', 'retired'] as const;

const MAX_BOND = 1_000_000_000;

/**
 * One wear-PLACE a hero declares (ADR-0099) — a hero-scoped equip slot. Its label is NOT authored here
 * (ADR-0110 §3): the compiler DERIVES `cosmetics.category.<id>` and reads its value from `strings.yaml`.places.
 */
const placeSchema = z
  .object({
    id: z.string().regex(SLUG),
  })
  .strict();

/**
 * The hero's ✨ currency SKIN (ADR-0103) — the kid-facing display `name` + `icon` for its effort/grow
 * currency. Optional: a hero without it falls back to the canonical ✨ skin (in {@link assemble}). The
 * shared spend/allowance currency ("coins") is never skinned.
 */
const currencySchema = z
  .object({
    nameKey: z.string().regex(I18N_KEY),
    iconRef: z.string().regex(SLUG),
  })
  .strict();

/**
 * One rung of a hero's finite ✨-LEVEL ladder (ADR-0103): the lifetime ✨ to reach it, and an OPTIONAL
 * milestone `labelKey` (the old form titles become labels on a level). A rung without a label is a plain
 * "Level N"; a rung with one carries a title (Apprentice / Enchantress / …) + its small celebration. The
 * ladder is the kid's headline progression — longer than the form ladder, with labels only at milestones.
 */
const levelSchema = z
  .object({
    stars: z.number().int().min(0).max(MAX_BOND),
    // The OPTIONAL milestone label is a stage INDEX (ADR-0110 §3): the compiler derives this rung's label key
    // `play.archetype.<slug>.stage.<stage>` and reads its value from `strings.yaml`.stages[stage]. A rung
    // without `stage` is a plain "Level N".
    stage: z.number().int().min(0).max(31).optional(),
  })
  .strict();

/**
 * The hero's DEFAULT-bundle identity (ADR-0100). Its slug IS the hero's archetypeId (the per-household
 * enablement key); this carries only the kid-facing title + catalog order. The default bundle's cosmetics
 * are the manifest's `looks` (ADR-0114), and its status is the hero's `status`.
 */
const defaultBundleSchema = z
  .object({
    // The title is NOT authored here (ADR-0110 §3) — the compiler derives `play.bundle.<archetypeId>.title`
    // and reads its value from the hero's `strings.yaml`.bundleTitle.
    sortOrder: z.number().int().min(0),
  })
  .strict();

/** One cosmetic look. Price is authored as a `tier` on the universal ladder (effort/coins derived — ADR-0127). */
const cosmeticSchema = z
  .object({
    slug: z.string().regex(SLUG),
    // The hero-scoped wear-place this look occupies (ADR-0099) — OPTIONAL (ADR-0115): omitted → the hero's
    // single derived `<archetypeId>_look`. Only a bespoke multi-place hero sets it. Validated ∈ the hero's
    // places at assemble time. Free-form slug here; the core attaches no meaning to it.
    place: z.string().regex(SLUG).optional(),
    // The look's price TIER in the COLLECTIBLE band (ADR-0127) — a small integer 1–4 (tier n ≈ n effort-days);
    // the compiler derives its `effortDays` + stable coin `cost`. A skin is never free (only starter heroes are).
    // Capped at 4 so the whole collectible band stays strictly below the hero floor (tier-1 hero = 5 effort-days,
    // pricing.ts:HERO_TIER1_EFFORT_DAYS) — the "bands never cross" invariant that pricing.ts documents.
    tier: z.number().int().min(1).max(4),
    art: z
      .object({ ref: z.string().min(1).max(200) })
      .strict()
      .optional(),
    /**
     * Per-look browse THEME slug (ADR-0113 Tier 1.5) — groups the hero's looks into named, art-first sets in
     * the skin shop (e.g. `regal` / `magic` / `nature`), so a hero's looks browse as themed shelves instead of
     * one long wall as the wardrobe grows. Its localized name is the i18n key `play.look.theme.<slug>`. Optional.
     */
    theme: z.string().regex(SLUG).optional(),
  })
  .strict();

/**
 * A hero's PROGRESSION UNLOCK RULE (ADR-0127) — an optional gate, ON TOP of the ✨ price, that a top-tier hero
 * uses to reward depth over grinding: the child must already OWN a hero of at least `heroTier` standing at
 * `minLevel` or above before this hero can be unlocked. Read at unlock time against the child's owned heroes'
 * per-hero ✨-levels. Absent → price is the only gate.
 */
const unlockRequiresSchema = z
  .object({
    heroTier: z.number().int().min(1).max(20),
    minLevel: z.number().int().min(1).max(20),
  })
  .strict();

/**
 * The hero CONFIG — `content/heroes/<hero>/manifest.yaml`, the play_content archetype payload, authored here
 * (ADR-0100 / ADR-0110 §2 / ADR-0114). It carries identity/status/places/forms/levels/pricing + the hero's own
 * `looks` (its DEFAULT-bundle cosmetics, inlined here per ADR-0114 — no separate `cosmetics/` file) — but NO
 * i18n keys: every kid-facing string (name, tagline, stage/place labels, cosmetic names) is authored in the
 * sibling `strings.yaml` and compiled to a drift-gated `packages/i18n` fragment (ADR-0110 §3).
 */
export const HeroFileSchema = z
  .object({
    archetypeId: z.string().regex(SLUG),
    // The `play.voice.<id>.*` namespace this hero's authored `strings.yaml`.voice emits under (ADR-0110 §4 /
    // ADR-0115). OPTIONAL — defaults to the archetypeId; set it only to reuse another hero's voice namespace.
    // (The voice PACK id is derived `voice_<archetypeId>` — the manifest no longer authors a `voicePackId`.)
    voiceI18nId: z.string().regex(SLUG).optional(),
    // The hero's lifecycle (ADR-0100) — drives its default bundle's status + the ≥1-hero `isDefault` set.
    status: z.enum(CONTENT_STATUSES).default('draft'),
    // The hero's default bundle (ADR-0100) — slug = archetypeId; its cosmetics are the `looks` below.
    defaultBundle: defaultBundleSchema,
    // The hero's wear-PLACES (ADR-0099) — OPTIONAL (ADR-0115). A skin hero (the rule) is full-look-swap with
    // ONE place, so it OMITS this and the compiler derives `[<archetypeId>_look]`. Only a bespoke mix-and-match
    // hero authors explicit places. A look's `place` must be one of these (derived → the single look-place).
    places: z.array(placeSchema).min(1).max(12).optional(),
    // The hero's OWN looks (ADR-0114) — its DEFAULT-bundle cosmetics, inlined here (was cosmetics/cosmetics.yaml).
    // A look's `place` is OPTIONAL (ADR-0115) — omitted → the hero's single derived `<archetypeId>_look`;
    // expansion bundles still live in `bundles/<name>/bundle.yaml`.
    looks: z.array(cosmeticSchema).default([]),
    // The hero's ✨ currency skin (ADR-0103) — optional; canonical ✨ fallback in assemble.
    currency: currencySchema.optional(),
    // The hero's finite ✨-LEVEL ladder (ADR-0103) — the hero's SOLE progression (the old finite evolution
    // `forms` retired in ADR-0115 §2b). `stage` marks a milestone rung's label; a label-less rung is "Level N".
    levels: z.array(levelSchema).min(1).max(20),
    // The hero's price TIER in the HERO band (ADR-0127). `0` = a day-one STARTER a kid owns from the start
    // (royal, prince); `≥ 1` = a saved-toward hero, priced in a band ABOVE every collectible. The compiler
    // derives its per-child ✨ price from the tier's `effortDays`; a tier-0 hero is never charged.
    tier: z.number().int().min(0).max(20),
    // An OPTIONAL progression gate (ADR-0127) — own a hero of ≥ `heroTier` at ≥ `minLevel` before unlocking this
    // one. Layered ON TOP of the ✨ price; used by the top (mythic) heroes so they reward depth, not just grinding.
    unlockRequires: unlockRequiresSchema.optional(),
  })
  .strict()
  .superRefine((hero, ctx) => {
    // `places` is OPTIONAL (ADR-0115) — a skin hero omits it and the compiler derives the single look-place.
    // This check runs only over what a bespoke hero authored (a no-op for a skin hero).
    // (0) place ids unique within the hero.
    const seenPlaces = new Set<string>();
    (hero.places ?? []).forEach((p, i) => {
      if (seenPlaces.has(p.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `duplicate place id "${p.id}"`,
          path: ['places', i, 'id'],
        });
      }
      seenPlaces.add(p.id);
    });
    // (5) the ✨-LEVEL ladder (ADR-0103), when authored: the first rung is reachable at 0 ✨ (level 1) and
    // thresholds STRICTLY INCREASE (a monotonic ladder — no two levels share a ✨ threshold).
    if (hero.levels !== undefined) {
      if (hero.levels[0] !== undefined && hero.levels[0].stars !== 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'the first level rung must be reachable at 0 ✨ (level 1)',
          path: ['levels', 0, 'stars'],
        });
      }
      let prevStars = -Infinity;
      hero.levels.forEach((rung, i) => {
        if (rung.stars <= prevStars) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `level ✨ thresholds must strictly increase (rung ${i}: ${rung.stars} ≤ ${prevStars})`,
            path: ['levels', i, 'stars'],
          });
        }
        prevStars = rung.stars;
      });
    }
  });

/**
 * An EXPANSION bundle source file — `content/<hero>/bundles/<name>/bundle.yaml` (ADR-0100, "bundle cosmetics
 * with the bundle"). A curated themed set of looks for the hero the directory belongs to (`heroArchetype` is
 * inferred from the dir, never authored here). Expansions are never the default bundle — the default is always
 * the hero's `cosmetics/`.
 */
export const BundleSourceSchema = z
  .object({
    bundleSlug: z.string().regex(SLUG),
    status: z.enum(CONTENT_STATUSES).default('draft'),
    sortOrder: z.number().int().min(0),
    cosmetics: z.array(cosmeticSchema).default([]),
  })
  .strict();

/**
 * One flat COLLECTIBLE (ADR-0114) — a cross-hero pet (ADR-0108) or background (ADR-0110 §7). Both kinds are the
 * SAME shape (one schema, instantiated per kind), much simpler than a hero: no places, forms, or level ladder —
 * just an identity, a price `tier`, and a catalog order. Its art is resolved by slug on the web art subpath
 * (`content/<kind>/art/<slug>.webp`). Every collectible is BOUGHT (ADR-0127) — its `tier` is a paid tier (no
 * free collectibles); the compiler derives its `effortDays` + coin `cost`, and the shop scales that per-child.
 * The `play.<kind>.theme.<slug>` / `play.color.<slug>` / `play.vibe.<slug>` keys the comments reference are
 * namespaced by the kind at compile time.
 */
/** One inline translation (ADR-0110 §3 · ADR-0115) — both locales authored together, beside the content. */
const localizedSchema = z.object({ en: z.string().min(1), uk: z.string().min(1) }).strict();

const flatCollectibleSchema = z
  .object({
    slug: z.string().regex(SLUG),
    // Co-located translations (ADR-0115) — the kid-facing name (+ optional tagline) authored inline as
    // { en, uk } beside the item; the compiler DERIVES play.<kind>.<slug>.name and emits the value (the
    // values used to live hand-maintained in packages/i18n; now they live with the data, like heroes).
    name: localizedSchema,
    tagline: localizedSchema.optional(),
    // The collectible's price TIER in the COLLECTIBLE band (ADR-0127) — a small integer 1–4 (tier n ≈ n
    // effort-days); the compiler derives its `effortDays` + coin `cost`, scaled per-child in the kid shop.
    // Capped at 4 so the collectible band stays strictly below the hero floor (tier-1 hero = 5 effort-days),
    // enforcing the "bands never cross" invariant documented in pricing.ts.
    tier: z.number().int().min(1).max(4),
    sortOrder: z.number().int().min(0),
    /** Per-type browse THEME slug (ADR-0113) — a named, art-first shop collection; `play.<kind>.theme.<slug>`. Optional. */
    theme: z.string().regex(SLUG).optional(),
    /** Release date (ISO `YYYY-MM-DD`, ADR-0113) — drives the shop's curated "New" shelf. Optional. */
    releasedAt: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      // The shape regex alone accepts calendar-nonsense (`2026-13-45`, `2026-00-00`) that would sort/filter
      // wrong in the "New" shelf. Round-trip through Date and require the parts survive, so a bad date fails
      // the compile loudly rather than shipping a mis-ordered shelf.
      .refine((s) => {
        const [y, m, d] = s.split('-').map(Number) as [number, number, number];
        const dt = new Date(Date.UTC(y, m - 1, d));
        return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
      }, 'releasedAt must be a real calendar date')
      .optional(),
    /** Dominant COLOR slug (ADR-0113 Tier 1.4) — the one picture-first filter swatch; label `play.color.<slug>`. Optional. */
    color: z.string().regex(SLUG).optional(),
    /** VIBE tags (ADR-0113 Tier 1.4) — a small set of mood slugs to filter by; each labels via `play.vibe.<slug>`. Optional. */
    tags: z.array(z.string().regex(SLUG)).max(8).optional(),
  })
  .strict();

/**
 * A flat-collectible catalog file — `content/<listKey>/<listKey>.yaml` (ADR-0114). The single per-kind
 * difference is the top-level list key (`pets:` vs `backgrounds:`), which the compiler supplies per
 * {@link COLLECTIBLE_KINDS} entry; the item shape is one schema. The compiler dedupes + orders.
 */
export const FlatCollectibleFileSchema = (listKey: string) =>
  z.object({ [listKey]: z.array(flatCollectibleSchema).default([]) }).strict();

/**
 * The flat-collectible TYPE REGISTRY (ADR-0114) — the kinds modelled by {@link flatCollectibleSchema}. Adding a
 * cross-hero collectible kind (frames, mounts, …) is one entry here + its `content/<listKey>/` dir (yaml + art)
 * + a kind branch in the web `flatCollectible` plugin — no new schema, assemble, or compiled type. `kind` is the
 * compiled discriminator + i18n/art namespace; `listKey` is the yaml file + its top-level array key.
 */
export const COLLECTIBLE_KINDS = [
  { kind: 'pet', listKey: 'pets' },
  { kind: 'background', listKey: 'backgrounds' },
] as const satisfies ReadonlyArray<{ kind: 'pet' | 'background'; listKey: string }>;

/** The collectible TYPES a browse theme can belong to (ADR-0113 Tier 1.3). */
export const THEME_TYPES = ['pet', 'background', 'look'] as const;

/**
 * One first-class browse THEME (ADR-0113 Tier 1.3 registry). A theme is authored ONCE here — its canonical
 * order (`sortOrder`), its cover (`coverSlug`, a representative item of the same type whose art fronts the
 * theme), and its localized name (the i18n key `play.{pet|background|look}.theme.<id>`, derived by the
 * compiler — no key authored here). The per-item `theme` slug references one of these. `(type, id)` is the
 * key — the same `id` may exist for two different types (e.g. a `magic` pet theme + a `magic` look theme).
 */
const themeSchema = z
  .object({
    id: z.string().regex(SLUG),
    type: z.enum(THEME_TYPES),
    // The theme's localized name (ADR-0115) — authored inline; the compiler DERIVES play.<type>.theme.<id>.
    name: localizedSchema,
    /** A representative item slug of the same type — its art fronts the theme's browse cover. */
    coverSlug: z.string().regex(SLUG),
    sortOrder: z.number().int().min(0),
  })
  .strict();

/** The themes registry file — `content/themes/themes.yaml` (ADR-0113 Tier 1.3). A flat list across all types. */
export const ThemesFileSchema = z
  .object({
    themes: z.array(themeSchema).default([]),
  })
  .strict();

/**
 * A VOICE TREE (ADR-0110 §4) — a nested namespace of narrator lines mirroring the i18n key path
 * (`reward.granted` → `reward: { granted: … }`). A LEAF is an ORDERED array of `{en,uk}` variants (paired so
 * `variantIndex` selects the same line in both locales). The compiler copies the tree verbatim under
 * `play.voice.<id>` (it does NOT split keys), so a literal-dot leaf key is preserved exactly.
 */
const voiceTreeSchema: z.ZodType<unknown> = z.lazy(() =>
  z.union([z.array(localizedSchema), z.record(z.string(), voiceTreeSchema)]),
);
const voicePackSchema = z.record(z.string(), voiceTreeSchema);

/**
 * The hero's co-located translations — `content/heroes/<hero>/strings.yaml` (ADR-0110 §3). Authored inline as
 * `{ en, uk }`; the compiler derives the stable runtime keys (`play.archetype.<slug>.name`,
 * `cosmetics.item.<slug>.name`, …) and emits BOTH the catalog keys AND a drift-gated `packages/i18n` fragment.
 * `stages` is ordered (index = stage number); `places`/`cosmetics` are keyed by the place id / cosmetic slug.
 */
export const StringsFileSchema = z
  .object({
    name: localizedSchema,
    tagline: localizedSchema.optional(),
    bundleTitle: localizedSchema,
    stages: z.array(localizedSchema).default([]),
    places: z.record(z.string().regex(SLUG), localizedSchema).default({}),
    cosmetics: z.record(z.string().regex(SLUG), localizedSchema).default({}),
    // The hero's narrator lines (ADR-0110 §4) — emitted under `play.voice.<manifest.voiceI18nId>.*`.
    voice: voicePackSchema.optional(),
  })
  .strict();

/**
 * The shared NEUTRAL content — `content/_core/strings.yaml` (ADR-0110 §2/§4). The fallback narrator voice pack
 * (`play.voice.neutral.*`), the canonical ✨ currency label (`play.currency.star`), and the neutral hero name
 * (`play.archetype.neutral.name`). Co-located here so `packages/i18n` holds none of it.
 */
export const CoreStringsFileSchema = z
  .object({
    // The ✨ label must DECLINE with a count (uk: 1 зірка / 2 зірки / 5 зірок), so it is authored per CLDR
    // plural form; the compiler emits `play.currency.star` (the count-less base = `other`) plus
    // `play.currency.star_<form>` so callers resolving with `{ count }` get the agreeing form.
    currency: z
      .object({
        star: z
          .object({
            one: localizedSchema,
            few: localizedSchema,
            many: localizedSchema,
            other: localizedSchema,
          })
          .strict(),
      })
      .strict(),
    neutralName: localizedSchema,
    voice: voicePackSchema.default({}),
  })
  .strict();

export type Localized = z.infer<typeof localizedSchema>;
export type StringsFile = z.infer<typeof StringsFileSchema>;
export type CoreStringsFile = z.infer<typeof CoreStringsFileSchema>;

export type HeroDef = z.infer<typeof HeroFileSchema>;
export type CosmeticDef = z.infer<typeof cosmeticSchema>;
export type BundleSource = z.infer<typeof BundleSourceSchema>;
export type FlatCollectibleDef = z.infer<typeof flatCollectibleSchema>;
export type ThemeDef = z.infer<typeof themeSchema>;
export type ThemesFile = z.infer<typeof ThemesFileSchema>;

/**
 * The COMPOSED hero the assembler consumes — the manifest CONFIG enriched with the i18n KEYS the compiler
 * DERIVES from the slug (ADR-0110 §3). The manifest no longer authors these keys; the compiler synthesizes
 * them here (and reads their VALUES from `strings.yaml` for the locale fragment), so `assemble.ts` + the
 * compiled JSON shape are UNCHANGED by the co-location reorg.
 */
export interface HeroComposed {
  readonly archetypeId: string;
  readonly nameKey: string;
  readonly taglineKey?: string;
  readonly voicePackId: string;
  /** The `play.voice.<id>.*` namespace this hero authors its narrator lines under (ADR-0110 §4); absent if it authors no voice. */
  readonly voiceI18nId?: string;
  readonly status: (typeof CONTENT_STATUSES)[number];
  readonly defaultBundle: { readonly titleKey: string; readonly sortOrder: number };
  readonly places: ReadonlyArray<{ readonly id: string; readonly nameKey: string }>;
  readonly currency?: { readonly nameKey: string; readonly iconRef: string };
  readonly levels: ReadonlyArray<{ readonly stars: number; readonly labelKey?: string }>;
  readonly tier: number;
  readonly unlockRequires?: { readonly heroTier: number; readonly minLevel: number };
}

/**
 * The COMPOSED bundle the assembler consumes — a manifest + ONE of its bundle files. The compiler attaches the
 * composed hero to the DEFAULT bundle (`kind: 'hero'`); expansion bundles are `kind: 'cosmetics'`. This
 * composition keeps `assemble.ts` and the compiled JSON shape unchanged across the authoring reorg.
 */
export type BundleFile = {
  bundleSlug: string;
  kind: 'hero' | 'cosmetics';
  heroArchetype: string;
  isDefault: boolean;
  status: (typeof CONTENT_STATUSES)[number];
  titleKey: string;
  sortOrder: number;
  hero?: HeroComposed;
  cosmetics: CosmeticDef[];
};
